import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  IconButton,
  Divider,
  FormControlLabel,
  Checkbox,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CustomButton from "../../Button/CustomButton";
import SaveIcon from "@mui/icons-material/Save";
import SearchIcon from "@mui/icons-material/Search";

const PhoneNumberModal = ({ open, onClose, data, onSave }) => {
  const isEdit = !!data;

  const [formData, setFormData] = useState({
    phoneNumber: "",
    friendlyName: "",
    country: "United States",
    state: "",
    city: "",
    type: "Local",
    capabilities: {
      voice: true,
      sms: true,
      mms: false,
    },
    assignedTo: "",
    forwardingNumber: "",
    voicemailEnabled: false,
    recordingEnabled: true,
  });

  const [searchMode, setSearchMode] = useState(!isEdit);

  useEffect(() => {
    if (data) {
      setFormData({
        phoneNumber: data.number || "",
        friendlyName: data.friendlyName || "",
        country: data.country || "United States",
        state: data.state || "",
        city: data.city || "",
        type: data.type || "Local",
        capabilities: data.capabilities || {
          voice: true,
          sms: true,
          mms: false,
        },
        assignedTo: data.assignedTo || "",
        forwardingNumber: data.forwardingNumber || "",
        voicemailEnabled: data.voicemailEnabled || false,
        recordingEnabled: data.recordingEnabled !== false,
      });
      setSearchMode(false);
    }
  }, [data]);

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleCapabilityChange = (capability, value) => {
    setFormData({
      ...formData,
      capabilities: {
        ...formData.capabilities,
        [capability]: value,
      },
    });
  };

  const handleSearchNumber = () => {
    // Simulate phone number search
    const areaCode = Math.floor(Math.random() * 900) + 100;
    const prefix = Math.floor(Math.random() * 900) + 100;
    const line = Math.floor(Math.random() * 9000) + 1000;
    const foundNumber = `+1 (${areaCode}) ${prefix}-${line}`;

    setFormData({ ...formData, phoneNumber: foundNumber });
    setSearchMode(false);
    alert(`Found available number: ${foundNumber}`);
  };

  const handleSubmit = () => {
    onSave(formData);
    handleClose();
  };

  const handleClose = () => {
    setFormData({
      phoneNumber: "",
      friendlyName: "",
      country: "United States",
      state: "",
      city: "",
      type: "Local",
      capabilities: {
        voice: true,
        sms: true,
        mms: false,
      },
      assignedTo: "",
      forwardingNumber: "",
      voicemailEnabled: false,
      recordingEnabled: true,
    });
    setSearchMode(!isEdit);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h5" fontWeight={600}>
            {isEdit ? "Edit Phone Number" : "Add Phone Number"}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {!isEdit && searchMode && (
            <>
              <Alert severity="info">
                Search for available phone numbers by location and type
              </Alert>

              <Box>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Search Criteria
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
                  <FormControl fullWidth>
                    <InputLabel>Country</InputLabel>
                    <Select
                      value={formData.country}
                      label="Country"
                      onChange={(e) => handleChange("country", e.target.value)}
                    >
                      <MenuItem value="United States">United States</MenuItem>
                      <MenuItem value="Canada">Canada</MenuItem>
                      <MenuItem value="United Kingdom">United Kingdom</MenuItem>
                      <MenuItem value="Australia">Australia</MenuItem>
                    </Select>
                  </FormControl>

                  <TextField
                    fullWidth
                    label="State/Province"
                    value={formData.state}
                    onChange={(e) => handleChange("state", e.target.value)}
                    placeholder="e.g., California"
                  />

                  <TextField
                    fullWidth
                    label="City"
                    value={formData.city}
                    onChange={(e) => handleChange("city", e.target.value)}
                    placeholder="e.g., Los Angeles"
                  />

                  <FormControl fullWidth>
                    <InputLabel>Number Type</InputLabel>
                    <Select
                      value={formData.type}
                      label="Number Type"
                      onChange={(e) => handleChange("type", e.target.value)}
                    >
                      <MenuItem value="Local">Local</MenuItem>
                      <MenuItem value="Toll-Free">Toll-Free</MenuItem>
                      <MenuItem value="Mobile">Mobile</MenuItem>
                    </Select>
                  </FormControl>

                  <CustomButton
                    icon={<SearchIcon />}
                    iconPosition="left"
                    text="Search Available Numbers"
                    onClick={handleSearchNumber}
                    fullWidth
                  />
                </Box>
              </Box>
            </>
          )}

          {(!searchMode || isEdit) && (
            <>
              {/* Phone Number Display/Edit */}
              <Box>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Number Information
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    value={formData.phoneNumber}
                    onChange={(e) => handleChange("phoneNumber", e.target.value)}
                    disabled={isEdit}
                    placeholder="+1 (555) 123-4567"
                  />
                  <TextField
                    fullWidth
                    label="Friendly Name"
                    value={formData.friendlyName}
                    onChange={(e) => handleChange("friendlyName", e.target.value)}
                    placeholder="e.g., Main Support Line"
                  />
                </Box>
              </Box>

              <Divider />

              {/* Capabilities */}
              <Box>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Capabilities
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.capabilities.voice}
                        onChange={(e) => handleCapabilityChange("voice", e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Voice Calls"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.capabilities.sms}
                        onChange={(e) => handleCapabilityChange("sms", e.target.checked)}
                        color="primary"
                      />
                    }
                    label="SMS Messaging"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.capabilities.mms}
                        onChange={(e) => handleCapabilityChange("mms", e.target.checked)}
                        color="primary"
                      />
                    }
                    label="MMS Messaging"
                  />
                </Box>
              </Box>

              <Divider />

              {/* Configuration */}
              <Box>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Configuration
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
                  <FormControl fullWidth>
                    <InputLabel>Assign to Assistant</InputLabel>
                    <Select
                      value={formData.assignedTo}
                      label="Assign to Assistant"
                      onChange={(e) => handleChange("assignedTo", e.target.value)}
                    >
                      <MenuItem value="">None</MenuItem>
                      <MenuItem value="Customer Support Agent">Customer Support Agent</MenuItem>
                      <MenuItem value="Sales Agent">Sales Agent</MenuItem>
                      <MenuItem value="Appointment Scheduler">Appointment Scheduler</MenuItem>
                      <MenuItem value="Technical Support">Technical Support</MenuItem>
                    </Select>
                  </FormControl>

                  <TextField
                    fullWidth
                    label="Forwarding Number (Optional)"
                    value={formData.forwardingNumber}
                    onChange={(e) => handleChange("forwardingNumber", e.target.value)}
                    placeholder="+1 (555) 987-6543"
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.voicemailEnabled}
                        onChange={(e) => handleChange("voicemailEnabled", e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Enable Voicemail"
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.recordingEnabled}
                        onChange={(e) => handleChange("recordingEnabled", e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Enable Call Recording"
                  />
                </Box>
              </Box>
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        {!isEdit && !searchMode && (
          <CustomButton
            text="Search Different Number"
            variant="outlined"
            onClick={() => setSearchMode(true)}
          />
        )}
        <Box sx={{ flex: 1 }} />
        <CustomButton text="Cancel" variant="outlined" onClick={handleClose} />
        {(!searchMode || isEdit) && (
          <CustomButton
            icon={<SaveIcon />}
            iconPosition="left"
            text={isEdit ? "Update Number" : "Purchase Number"}
            onClick={handleSubmit}
            disabled={!formData.phoneNumber.trim()}
          />
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PhoneNumberModal;
